import {
  createContext,
  memo,
  useContext,
  useState,
  type ReactNode,
} from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconGripVertical } from "../../../components/icons";
import type { ApiFieldTypeOption } from "../../../lib/formBuilder/apiFieldTypes";
import type {
  FormBuilderField,
  FormBuilderSection,
} from "../../../types/formBuilder";
import { FieldEditor } from "./FieldEditor";

type SortableFieldListProps = {
  fields: FormBuilderField[];
  fieldNames: string[];
  builderFields: FormBuilderField[];
  fieldTypes: ApiFieldTypeOption[];
  canDrag: boolean;
  onChange: (fieldClientId: string, patch: Partial<FormBuilderField>) => void;
  onRemove: (fieldClientId: string) => void;
  onMove: (fieldClientId: string, direction: -1 | 1) => void;
  canDuplicate: boolean;
  onDuplicate: (fieldClientId: string) => void;
  quizMode?: boolean;
};

const PlaceholderHeightContext = createContext(72);

const fieldCollisionDetection: CollisionDetection = (args) => {
  const collisions = pointerWithin(args);
  if (collisions.length > 0) {
    const fields = collisions.filter(
      ({ id }) => !String(id).startsWith("section:"),
    );
    return fields.length > 0 ? fields : collisions;
  }
  return closestCenter(args);
};

export function FieldDndProvider({
  sections,
  fieldTypes,
  onDrop,
  children,
}: {
  sections: FormBuilderSection[];
  fieldTypes: ApiFieldTypeOption[];
  onDrop: (
    fieldClientId: string,
    sectionClientId: string,
    targetIndex: number,
  ) => void;
  children: ReactNode;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [placeholderHeight, setPlaceholderHeight] = useState(72);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    setPlaceholderHeight(event.active.rect.current.initial?.height ?? 72);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const sourceSection = sections.find((section) =>
      section.fields.some((field) => field.clientId === active.id),
    );
    if (!sourceSection) return;

    const overId = String(over.id);
    if (overId.startsWith("section:")) {
      const targetSection = sections.find(
        (section) => section.clientId === overId.slice("section:".length),
      );
      if (targetSection) {
        onDrop(
          String(active.id),
          targetSection.clientId,
          targetSection.fields.length,
        );
      }
      return;
    }

    const targetSection = sections.find((section) =>
      section.fields.some((field) => field.clientId === over.id),
    );
    if (!targetSection) return;

    const overIndex = targetSection.fields.findIndex(
      (field) => field.clientId === over.id,
    );
    const draggedRect = active.rect.current.translated;
    const insertAfter =
      sourceSection !== targetSection &&
      draggedRect !== null &&
      draggedRect.top + draggedRect.height / 2 >
        over.rect.top + over.rect.height / 2;
    onDrop(
      String(active.id),
      targetSection.clientId,
      overIndex + (insertAfter ? 1 : 0),
    );
  };

  const activeField = activeId
    ? sections
        .flatMap((section) => section.fields)
        .find((field) => field.clientId === activeId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={fieldCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <PlaceholderHeightContext.Provider value={placeholderHeight}>
        {children}
      </PlaceholderHeightContext.Provider>
      <DragOverlay dropAnimation={{ duration: 160, easing: "ease-out" }}>
        {activeField ? (
          <FieldDragPreview field={activeField} fieldTypes={fieldTypes} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function fieldTypeLabel(
  field: FormBuilderField,
  fieldTypes: ApiFieldTypeOption[],
): string {
  return (
    fieldTypes.find((item) => item.value === field.type)?.label ??
    String(field.type)
  );
}

function FieldDragPreview({
  field,
  fieldTypes,
}: {
  field: FormBuilderField;
  fieldTypes: ApiFieldTypeOption[];
}) {
  const label = field.label.trim() || field.name || "Без названия";

  return (
    <article className="cursor-grabbing rounded-lg border border-brand-300 bg-white p-3 shadow-lg shadow-slate-900/10 ring-2 ring-brand-200/40">
      <div className="flex items-center gap-2">
        <IconGripVertical size={14} className="shrink-0 text-slate-400" />
        <span className="truncate font-onest text-sm font-semibold text-slate-800">
          {label}
        </span>
        <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-onest text-[10px] text-slate-500">
          {fieldTypeLabel(field, fieldTypes)}
        </span>
      </div>
    </article>
  );
}

const SortableFieldItem = memo(function SortableFieldItem({
  field,
  fieldIndex,
  fieldCount,
  fieldNames,
  builderFields,
  fieldTypes,
  canDrag,
  onChange,
  onRemove,
  onMove,
  canDuplicate,
  onDuplicate,
  quizMode = false,
}: {
  field: FormBuilderField;
  fieldIndex: number;
  fieldCount: number;
  fieldNames: string[];
  builderFields: FormBuilderField[];
  fieldTypes: ApiFieldTypeOption[];
  canDrag: boolean;
  onChange: (patch: Partial<FormBuilderField>) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
  canDuplicate: boolean;
  onDuplicate: () => void;
  quizMode?: boolean;
}) {
  const placeholderHeight = useContext(PlaceholderHeightContext);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: field.clientId,
    disabled: !canDrag,
    animateLayoutChanges: () => false,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        isOver && !isDragging ? "rounded-lg ring-2 ring-brand-300" : ""
      }
    >
      {isDragging ? (
        <div
          className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/90"
          style={{ height: placeholderHeight }}
          aria-hidden
        />
      ) : (
        <div>
          <FieldEditor
            field={field}
            fieldNames={fieldNames}
            builderFields={builderFields}
            fieldTypes={fieldTypes}
            canMoveUp={fieldIndex > 0}
            canMoveDown={fieldIndex < fieldCount - 1}
            dragHandleProps={
              canDrag
                ? { ref: setActivatorNodeRef, attributes, listeners }
                : undefined
            }
            onChange={onChange}
            onRemove={onRemove}
            canDuplicate={canDuplicate}
            onDuplicate={onDuplicate}
            quizMode={quizMode}
            onMoveUp={() => onMove(-1)}
            onMoveDown={() => onMove(1)}
          />
        </div>
      )}
    </div>
  );
});

export function SortableFieldList({
  fields,
  fieldNames,
  builderFields,
  fieldTypes,
  canDrag,
  onChange,
  onRemove,
  onMove,
  canDuplicate,
  onDuplicate,
  quizMode = false,
}: SortableFieldListProps) {
  return (
    <SortableContext
      items={fields.map((field) => field.clientId)}
      strategy={verticalListSortingStrategy}
    >
      <div className="space-y-2">
        {fields.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center font-onest text-xs text-slate-500">
            Нет полей — добавьте первое
          </p>
        ) : (
          fields.map((field, fieldIndex) => (
            <SortableFieldItem
              key={field.clientId}
              field={field}
              fieldIndex={fieldIndex}
              fieldCount={fields.length}
              fieldNames={fieldNames}
              builderFields={builderFields}
              fieldTypes={fieldTypes}
              canDrag={canDrag}
              onChange={(patch) => onChange(field.clientId, patch)}
              onRemove={() => onRemove(field.clientId)}
              onMove={(direction) => onMove(field.clientId, direction)}
              canDuplicate={canDuplicate}
              onDuplicate={() => onDuplicate(field.clientId)}
              quizMode={quizMode}
            />
          ))
        )}
      </div>
    </SortableContext>
  );
}
